import React, { useEffect, useRef, useLayoutEffect, forwardRef, useImperativeHandle } from 'react'
import '../monacoSetup'
import { Box, useColorModeValue } from '@chakra-ui/react'
import * as monaco from 'monaco-editor'
// @ts-ignore
import { initVimMode } from 'monaco-vim'
// @ts-ignore
import initEmacsMode from 'monaco-emacs'

// Add custom CSS for Monaco editor scrollbars and Vim status bar
const monacoScrollbarStyles = `
  .monaco-editor .scrollbar .slider {
    background: rgba(100, 100, 100, 0.4) !important;
  }
  .monaco-editor .scrollbar .slider:hover {
    background: rgba(100, 100, 100, 0.7) !important;
  }
  .monaco-editor .scrollbar .slider.active {
    background: rgba(100, 100, 100, 0.8) !important;
  }
  
  /* Ensure Vim status bar doesn't cause layout shifts or scrolling */
  .vim-status-bar {
    position: relative !important;
    z-index: 10 !important;
    overflow: hidden !important;
    user-select: none !important;
  }
  
  /* Allow pointer events for the status bar but prevent scroll behavior */
  .vim-status-bar input,
  .vim-status-bar textarea {
    user-select: text !important;
    scroll-margin: 0 !important;
    scroll-padding: 0 !important;
  }
  
  /* Prevent Vim status bar from causing page scroll */
  .vim-status-bar {
    scroll-margin: 0 !important;
    scroll-padding: 0 !important;
    scroll-behavior: auto !important;
  }
  
  /* Prevent any element within the Vim status bar from causing scroll */
  .vim-status-bar * {
    scroll-margin: 0 !important;
    scroll-padding: 0 !important;
  }
  
  /* Ensure Monaco editor container prevents scroll propagation */
  .monaco-editor-container {
    scroll-behavior: auto !important;
  }
  
  /* Assembly view line highlighting - works with light/dark themes */
  .assembly-highlighted-line {
    background-color: rgba(59, 130, 246, 0.1) !important;
    border-left: 3px solid rgba(59, 130, 246, 0.8) !important;
  }
  
  .assembly-highlighted-line-content {
    background-color: rgba(59, 130, 246, 0.05) !important;
  }
  
  /* Assembly selected line highlighting */
  .assembly-selected-line {
    background-color: rgba(249, 115, 22, 0.15) !important;
    border-left: 3px solid rgba(249, 115, 22, 0.8) !important;
  }
  
  /* Dark theme adjustments */
  .vs-dark .assembly-highlighted-line {
    background-color: rgba(59, 130, 246, 0.2) !important;
  }
  
  .vs-dark .assembly-selected-line {
    background-color: rgba(249, 115, 22, 0.25) !important;
  }
`

// Inject styles if not already present
if (!document.getElementById('monaco-scrollbar-styles')) {
  const styleElement = document.createElement('style')
  styleElement.id = 'monaco-scrollbar-styles'
  styleElement.textContent = monacoScrollbarStyles
  document.head.appendChild(styleElement)
}

export interface CodeEditorHandle {
  getValue: () => string
  setValue: (value: string) => void
  focus: () => void
}

export interface CodeEditorProps {
  value?: string
  onChange?: (value: string) => void
  language?: string
  height?: string
  readOnly?: boolean
  keybindingMode?: 'default' | 'vim' | 'emacs'
  // Assembly view highlighting
  highlightedLine?: number | null
  highlightVersion?: number // Force re-highlight even if line number is same
  onLineClick?: (lineNumber: number) => void
  // Uncontrolled usage
  defaultValue?: string
}

function CodeEditorInternal({
  value,
  onChange,
  language = 'cpp',
  height = '100%',
  readOnly = false,
  keybindingMode = 'default',
  highlightedLine,
  highlightVersion = 0,
  onLineClick,
  defaultValue,
}: CodeEditorProps, ref: React.Ref<CodeEditorHandle>) {
  const editorRef = useRef<HTMLDivElement>(null)
  const vimStatusBarRef = useRef<HTMLDivElement>(null)
  const editorInstanceRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const vimModeRef = useRef<any>(null)
  const emacsModeRef = useRef<any>(null)
  const decorationIdsRef = useRef<string[]>([])

  // Theme support
  const currentTheme = useColorModeValue('vs', 'vs-dark')

  useEffect(() => {
    if (editorRef.current && !editorInstanceRef.current) {
      // Ensure value is a valid string before creating editor
      const initialContent = value !== undefined ? value : defaultValue
      const safeValue = typeof initialContent === 'string' ? initialContent : String(initialContent || '')
      
      editorInstanceRef.current = monaco.editor.create(editorRef.current, {
        value: safeValue,
        language,
        theme: currentTheme,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: true,
        fontSize: 14,
        lineNumbers: 'on',
        roundedSelection: false,
        scrollbar: {
          vertical: 'visible',
          horizontal: 'visible',
          verticalScrollbarSize: 14,
          horizontalScrollbarSize: 14,
          useShadows: true,
        },
        wordWrap: 'on',
        folding: true,
        lineDecorationsWidth: 10,
        lineNumbersMinChars: 3,
        glyphMargin: false,
        renderLineHighlight: 'all',
        selectOnLineNumbers: true,
        codeLens: false,
        suggestOnTriggerCharacters: true,
        acceptSuggestionOnCommitCharacter: true,
        acceptSuggestionOnEnter: 'on',
        wordBasedSuggestions: true,
        parameterHints: {
          enabled: true,
        },
        autoIndent: 'full',
        formatOnPaste: true,
        formatOnType: true,
        readOnly: readOnly,
        find: {
          addExtraSpaceOnTop: false,
          autoFindInSelection: 'never',
          seedSearchStringFromSelection: 'never',
        },
      })

      editorInstanceRef.current.onDidChangeModelContent(() => {
        const newValue = editorInstanceRef.current?.getValue() || ''
        if (onChange) onChange(newValue)
      })

      // Add click handler for line selection
      if (onLineClick) {
        editorInstanceRef.current.onMouseDown((e) => {
          if (e.target.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS || 
              e.target.type === monaco.editor.MouseTargetType.CONTENT_TEXT) {
            const position = e.target.position
            if (position) {
              onLineClick(position.lineNumber - 1) // Convert to 0-based index
            }
          }
        })
      }
    }

    return () => {
      if (vimModeRef.current) {
        try {
          vimModeRef.current.dispose()
        } catch (error) {
          console.warn('Error disposing Vim mode:', error)
        }
        vimModeRef.current = null
      }
      if (emacsModeRef.current) {
        try {
          emacsModeRef.current.dispose()
        } catch (error) {
          console.warn('Error disposing Emacs mode:', error)
        }
        emacsModeRef.current = null
      }
      
      if (editorInstanceRef.current) {
        editorInstanceRef.current.dispose()
        editorInstanceRef.current = null
      }
    }
  }, [language, onChange, currentTheme])

  // Handle theme changes
  useEffect(() => {
    if (editorInstanceRef.current) {
      monaco.editor.setTheme(currentTheme)
    }
  }, [currentTheme])

  // Handle keybinding mode changes
  useLayoutEffect(() => {
    if (!editorInstanceRef.current) return

    // Clean up existing modes
    if (vimModeRef.current) {
      try {
        vimModeRef.current.dispose()
      } catch (error) {
        console.warn('Error disposing Vim mode:', error)
      }
      vimModeRef.current = null
    }
    if (emacsModeRef.current) {
      try {
        emacsModeRef.current.dispose()
      } catch (error) {
        console.warn('Error disposing Emacs mode:', error)
      }
      emacsModeRef.current = null
    }

    // Initialize Vim mode
    if (keybindingMode === 'vim') {
      try {

        // Always use the status bar element to prevent layout shifts
        if (vimStatusBarRef.current) {
          vimModeRef.current = initVimMode(editorInstanceRef.current, vimStatusBarRef.current)
          // Ensure the status bar is visible and properly positioned
          vimStatusBarRef.current.style.visibility = 'visible'
        } else {
          // Fallback if status bar ref is not available
          vimModeRef.current = initVimMode(editorInstanceRef.current, {
            useSystemClipboard: false,
          })
        }

      } catch (error) {
        console.error('Failed to initialize Vim mode:', error)
      }
    } else if (keybindingMode === 'emacs') {
      try {

        emacsModeRef.current = new initEmacsMode(editorInstanceRef.current)

      } catch (error) {
        console.error('Failed to initialize Emacs mode:', error)
      }
    } else {

      // Hide the Vim status bar when not in Vim mode
      if (vimStatusBarRef.current) {
        vimStatusBarRef.current.style.visibility = 'hidden'
      }
    }
  }, [keybindingMode])

  // Controlled updates only when "value" prop is provided
  useEffect(() => {
    if (value === undefined) return
    if (editorInstanceRef.current && value !== editorInstanceRef.current.getValue()) {
      const safeValue = typeof value === 'string' ? value : String(value || '')
      try {
        editorInstanceRef.current.setValue(safeValue)
      } catch (error) {
        console.error('Error setting editor value:', error, 'Value:', safeValue)
        try {
          editorInstanceRef.current.setValue('')
        } catch (fallbackError) {
          console.error('Even fallback setValue failed:', fallbackError)
        }
      }
    }
  }, [value])

  // Expose imperative handle for uncontrolled usage
  useImperativeHandle(ref, () => ({
    getValue: () => editorInstanceRef.current?.getValue() || '',
    setValue: (v: string) => {
      if (editorInstanceRef.current) {
        try {
          editorInstanceRef.current.setValue(v ?? '')
        } catch (error) {
          console.error('Failed to set editor value via handle:', error)
        }
      }
    },
    focus: () => {
      editorInstanceRef.current?.focus()
    }
  }), [])

  // Prevent Vim status bar from causing scroll issues
  useEffect(() => {
    if (keybindingMode === 'vim' && vimStatusBarRef.current) {
      const statusBar = vimStatusBarRef.current
      
      // Prevent scroll into view behavior without interfering with input functionality
      const preventScrollIntoView = (e: Event) => {
        // Only prevent scroll behavior, not focus or input events
        if (e.type === 'scroll') {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      
      // Listen for scroll events on the status bar
      statusBar.addEventListener('scroll', preventScrollIntoView, true)
      
      // Prevent the status bar container from being focusable
      statusBar.setAttribute('tabindex', '-1')
      statusBar.style.outline = 'none'
      
      return () => {
        statusBar.removeEventListener('scroll', preventScrollIntoView, true)
      }
    }
  }, [keybindingMode])

  // Handle line highlighting
  useEffect(() => {
    if (editorInstanceRef.current && highlightedLine !== undefined) {
      if (highlightedLine === null) {
        // Clear existing decorations
        decorationIdsRef.current = editorInstanceRef.current.deltaDecorations(decorationIdsRef.current, [])
      } else {
        // Add highlight decoration to the specified line
        const decorations = [{
          range: new monaco.Range(highlightedLine + 1, 1, highlightedLine + 1, 1), // Convert to 1-based
          options: {
            isWholeLine: true,
            className: 'assembly-highlighted-line',
            inlineClassName: 'assembly-highlighted-line-content'
          }
        }]
        // Replace old decorations with new ones
        decorationIdsRef.current = editorInstanceRef.current.deltaDecorations(decorationIdsRef.current, decorations)
      }
      
      // Ensure theme consistency after decoration changes
      monaco.editor.setTheme(currentTheme)
    }
  }, [highlightedLine, highlightVersion])

  return (
    <Box 
      h={height} 
      w="100%" 
      borderRadius="md" 
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      display="flex"
      flexDirection="column"
    >
      <Box ref={editorRef} flex="1" w="100%" overflow="hidden" className="monaco-editor-container" />
      <Box 
        ref={vimStatusBarRef}
        h="20px" 
        bg="gray.100" 
        borderTop="1px solid" 
        borderColor="gray.200"
        px={2}
        fontSize="12px"
        visibility={keybindingMode === 'vim' ? "visible" : "hidden"}
        display="flex"
        alignItems="center"
        fontFamily="monospace"
        flexShrink={0}
        className="vim-status-bar"
      />
    </Box>
  )
}

const CodeEditor = forwardRef<CodeEditorHandle, CodeEditorProps>(CodeEditorInternal)
export default CodeEditor